import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { MealListView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PageHeader,
  PrintFooter,
  PrintHeader,
  PrintSheet,
  printSheet,
} from '@wodi/ui';
import { useRef } from 'react';

import { mealListQuery, recordMealTicketsPrinted } from '../api/kitchen.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dayText, errorText, LoadFailed, NotAllowed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import tickets from './kitchen.module.css';
import { allergyWords, bedWords, dietWords, fluidWords, patientWords } from './words.tsx';

// Printable meal tickets (docs/plans/phase3-kitchen-and-housekeeping.md §1.4): one per patient
// to be served at the meal; nil-by-mouth patients get none. A4, black on white. Printing is
// audited with the ward, day, meal and count.

const route = getRouteApi('/_app/kitchen/tickets');

function Tickets({ list }: { list: MealListView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const sheet = useRef<HTMLElement>(null);
  const print = useMutation({
    mutationFn: () =>
      recordMealTicketsPrinted({
        wardId: list.ward?.id ?? null,
        date: list.date,
        meal: list.meal,
        tickets: list.serve.length,
      }),
    onSuccess: () => {
      if (sheet.current !== null) printSheet(sheet.current);
    },
  });
  const title = `${list.mealLabel} tickets, ${dayText(list.date)}`;
  return (
    <>
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!online || list.serve.length === 0}
          isPending={print.isPending}
          pendingLabel="Preparing…"
          onPress={() => {
            print.mutate();
          }}
        >
          Print meal tickets
        </Button>
      </div>
      {print.error === null ? null : (
        <Notice tone="error">
          {errorText(print.error, "The tickets weren't printed. Try again.")}
        </Notice>
      )}
      {list.nilByMouth.length > 0 ? (
        <Notice tone="info">
          {`${String(list.nilByMouth.length)} nil by mouth at this meal: no ticket is printed for them.`}
        </Notice>
      ) : null}
      <div>
        <PrintSheet paper="a4" label="Meal tickets" ref={sheet}>
          <PrintHeader
            facility={session?.facility.name ?? ''}
            title={list.ward === null ? title : `${title}, ${list.ward.name}`}
          />
          <ul className={tickets.tickets} aria-label="Meal tickets">
            {list.serve.map((r) => (
              <li key={r.admissionId} className={tickets.ticket}>
                <span className={tickets.ticketBed}>{`${bedWords(r)}, ${r.wardName}`}</span>
                <p className={tickets.ticketLine}>{patientWords(r)}</p>
                <p className={tickets.ticketLine}>
                  <span className={tickets.ticketLabel}>Diet: </span>
                  {dietWords(r)}
                </p>
                <p className={tickets.ticketLine}>
                  <span className={tickets.ticketLabel}>Fluids: </span>
                  {fluidWords(r.diet?.fluidRestrictionMl ?? null)}
                </p>
                <p className={tickets.ticketLine}>
                  <span className={tickets.ticketLabel}>Allergies: </span>
                  {allergyWords(r.allergies)}
                </p>
                {r.diet?.kitchenNote == null ? null : (
                  <p className={tickets.ticketLine}>
                    <span className={tickets.ticketLabel}>Note: </span>
                    {r.diet.kitchenNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <PrintFooter>Check the bed and the allergies before serving.</PrintFooter>
        </PrintSheet>
      </div>
    </>
  );
}

export function MealTicketsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const search = route.useSearch();
  const allowed = session !== null && can(session, 'kitchen.read');
  const list = useQuery({
    ...mealListQuery(search.wardId ?? null, search.date, search.meal),
    enabled: allowed,
  });
  if (!allowed) return <NotAllowed title="Meal tickets" what="print meal tickets" />;
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/kitchen">Back to the kitchen</Link>
      </p>
      <PageHeader title="Meal tickets" description="One ticket for each patient to be served." />
      {list.isPending ? <LoadingBlock label="Loading the meal tickets" rows={4} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="The meal tickets couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess ? <Tickets list={list.data} /> : null}
    </div>
  );
}
